// @ts-strict-ignore
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { getDaffyFetchOptions } from '~/helpers/api';
import log from '~/helpers/logging';
import { fetchJwtToken } from './fetchTokens';
import type { Cells, CustomerRbacType } from '../types';
import type { Agent } from '~/application-review/ApplicationReviewTypes';
import { routes } from '~/helpers/routes';

type Options = Omit<
UseQueryOptions<CustomerRbacType[]>,
'queryKey' | 'queryFn'
>;

/**
 * To support rbac on the provisioning client
 *
 * The function requests rbac access data for a given user for each specified in cells array
 */
export const useGetRbacCustomers = (
  { cells, agent }: { cells: Cells; agent: Agent },
  options?: Options,
) => useQuery({
  queryKey: [routes.v3.admin.provisioning_access_info, cells],
  queryFn: async () => {
    const rbacRequestConfig = agent?.isAfefParentAgent
      ? {
        route: routes.v3.admin.provisioning_afef_access_info,
        token: undefined,
      }
      : {
        route: routes.v3.admin.provisioning_access_info,
        token: undefined,
      };

    if (!agent?.isAfefParentAgent) {
      const jwtToken = await fetchJwtToken();
      rbacRequestConfig.token = jwtToken;
    }

    const rbacPromises = Object.values(cells)?.map(cell => fetch(`${cell.appUrl}${rbacRequestConfig.route}`, {
      ...getDaffyFetchOptions({
        headers: {
          'Content-type': 'application/json',
          ...(rbacRequestConfig.token
            ? { Authorization: `Bearer ${rbacRequestConfig.token}` }
            : {}),
        },
      }),
      credentials: 'include',
      signal: AbortSignal.timeout(5_000),
    }),
    );

    // This section of code needs another iteration
    // Could be exchanged for Promise.allSettled but thats "not a function" on the frontend
    const responses = await Promise.all(
      rbacPromises.map(async fetchResponse => {
        try {
          const response = await fetchResponse;

          if (!response.ok || response.status !== 200) {
            return undefined;
          }

          return (await response.json()) as { data: CustomerRbacType[] };
        } catch (err) {
          log('WARNING', 'Failed to fetch RBAC data', err);
          return undefined;
        }
      }),
    );

    return responses.flatMap(response => response?.data ?? []);
  },
  ...options,
});
